import { useState, type FormEvent } from "react";
import { useContextoApp } from "@/app/contexto";
import { useApiResource } from "@/hooks/useApiResource";
import { useAsyncAction } from "@/hooks/useAsyncAction";
import { api, type HitoObra, type SubHito } from "@/lib/api";
import { validarDescripcionHito, validarNombreHito } from "@/lib/avance";

/** Lo que se edita o borra: un hito de arriba o un sub-hito (con el hito al que pertenece). */
export type Objetivo = { hito: HitoObra; sub: SubHito | null };

type Dialogo =
  | { tipo: "form"; padre: HitoObra | null; editando: Objetivo | null } // editando null = alta (de hito o, con padre, de sub-hito)
  | { tipo: "borrar"; objetivo: Objetivo }
  | null;

/**
 * Lógica de Configuración → Avance de obra (sin JSX). Sólo admin: los hitos en orden, cada uno con sus sub-hitos, que
 * arman el recorrido de la obra que ven los fiduciantes. Alta, edición, subir/bajar y baja; cada escritura devuelve el
 * árbol entero, que reemplaza al que había. Los sub-hitos se marcan como cumplidos desde Avance, no desde acá.
 */
export function useAvanceObraTab() {
  const { fid } = useContextoApp();
  const base = `/api/fideicomisos/${fid}/avance/hitos`;
  const lista = useApiResource<HitoObra[]>(base);
  const [arbol, setArbol] = useState<HitoObra[] | null>(null);
  const [dialogo, setDialogo] = useState<Dialogo>(null);
  const [nombre, setNombre] = useState("");
  const [descripcion, setDescripcion] = useState("");
  const [mostrarErrores, setMostrarErrores] = useState(false);

  const hitos = arbol ?? lista.data ?? [];

  const accionGuardar = useAsyncAction((d: Exclude<Dialogo, null> & { tipo: "form" }, n: string, desc: string) => {
    const datos = { nombre: n.trim(), descripcion: desc.trim() || null };
    if (d.editando) return api.patch<HitoObra[]>(`${base}/${(d.editando.sub ?? d.editando.hito).id}`, datos);
    return api.post<HitoObra[]>(base, { ...datos, padre_id: d.padre?.id ?? null });
  });
  const accionSugeridos = useAsyncAction(() => api.post<HitoObra[]>(`${base}/sugeridos`, {}));
  const accionMover = useAsyncAction((id: number, hacia: "arriba" | "abajo") => api.post<HitoObra[]>(`${base}/${id}/mover`, { hacia }));
  const accionBorrar = useAsyncAction((id: number) => api.delete<void>(`${base}/${id}`).then(() => true));

  const errorNombre = validarNombreHito(nombre);
  const errorDescripcion = validarDescripcionHito(descripcion);
  const cerrar = () => setDialogo(null);

  const abrirForm = (padre: HitoObra | null, editando: Objetivo | null) => {
    const actual = editando ? (editando.sub ?? editando.hito) : null;
    setNombre(actual?.nombre ?? "");
    setDescripcion(actual?.descripcion ?? "");
    setMostrarErrores(false);
    accionGuardar.limpiarError();
    setDialogo({ tipo: "form", padre, editando });
  };

  const guardar = async (e: FormEvent) => {
    e.preventDefault();
    setMostrarErrores(true);
    if (dialogo?.tipo !== "form" || errorNombre || errorDescripcion) return;
    const nuevo = await accionGuardar.run(dialogo, nombre, descripcion);
    if (nuevo) {
      setArbol(nuevo);
      cerrar();
    }
  };

  const mover = async (id: number, hacia: "arriba" | "abajo") => {
    const nuevo = await accionMover.run(id, hacia);
    if (nuevo) setArbol(nuevo);
  };

  const confirmarBorrar = async () => {
    if (dialogo?.tipo !== "borrar") return;
    if (await accionBorrar.run((dialogo.objetivo.sub ?? dialogo.objetivo.hito).id)) {
      cerrar();
      setArbol(null);
      lista.recargar();
    }
  };

  return {
    hitos,
    cargando: lista.loading && !lista.data && !arbol,
    errorCarga: lista.data || arbol ? null : (lista.error?.message ?? null),
    reintentar: lista.recargar,
    dialogo,
    cerrar,
    nombre,
    setNombre,
    descripcion,
    setDescripcion,
    errorNombre: mostrarErrores ? errorNombre : null,
    errorDescripcion: mostrarErrores ? errorDescripcion : null,
    abrirNuevoHito: () => abrirForm(null, null),
    abrirNuevoSub: (hito: HitoObra) => abrirForm(hito, null),
    abrirEditar: (objetivo: Objetivo) => abrirForm(null, objetivo),
    guardar,
    guardando: accionGuardar.loading,
    errorGuardar: accionGuardar.error,
    subir: (id: number) => void mover(id, "arriba"),
    bajar: (id: number) => void mover(id, "abajo"),
    /** Carga los 14 pasos del recorrido del desarrollo (socio de Mati, 2026-11-03); sólo con la lista vacía. */
    cargarSugeridos: async () => {
      const nuevo = await accionSugeridos.run();
      if (nuevo) setArbol(nuevo);
    },
    cargandoSugeridos: accionSugeridos.loading,
    errorSugeridos: accionSugeridos.error,
    moviendo: accionMover.loading,
    errorMover: accionMover.error,
    abrirBorrar: (objetivo: Objetivo) => {
      accionBorrar.limpiarError();
      setDialogo({ tipo: "borrar", objetivo });
    },
    confirmarBorrar,
    borrando: accionBorrar.loading,
    errorBorrar: accionBorrar.error,
  };
}
